import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Image as ImageIcon, ChevronLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const PhotoGallerySection = () => {
  const [galleries, setGalleries] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGalleries();
  }, []);

  const fetchGalleries = async () => {
    try {
      const res = await axios.get(`${API}/galleries?visible_only=true&limit=6`);
      setGalleries(res.data || []);
    } catch (err) {
      console.error("Error fetching galleries:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || galleries.length === 0) return null;

  return (
    <section className="py-12 md:py-16 bg-zinc-900" data-testid="photo-gallery-section">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl md:text-4xl font-heading uppercase tracking-tight text-white">
            Photo Gallery
          </h2>
          <Link 
            to="/galleries" 
            className="text-orange-500 hover:text-orange-400 flex items-center gap-2 text-sm uppercase font-bold"
          >
            View All <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {galleries.slice(0, 6).map((gallery) => (
            <Link
              key={gallery.id}
              to={`/galleries/${gallery.id}`}
              className="group relative aspect-[4/3] overflow-hidden bg-zinc-800"
            >
              {gallery.cover_image_url ? (
                <img
                  src={gallery.cover_image_url}
                  alt={gallery.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon className="w-12 h-12 text-zinc-700" />
                </div>
              )}
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity" />
              
              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-4">
                {gallery.category && (
                  <span className="text-orange-500 text-xs font-bold uppercase tracking-wider mb-1 block">
                    {gallery.category}
                  </span>
                )}
                <h3 className="text-white font-heading text-lg uppercase tracking-tight line-clamp-2">
                  {gallery.title}
                </h3>
                {gallery.images && (
                  <span className="text-zinc-400 text-xs mt-1 block">
                    {gallery.images.length} {gallery.images.length === 1 ? "photo" : "photos"}
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button
              className="absolute top-4 right-4 text-white hover:text-orange-500 text-2xl"
              onClick={() => setSelectedImage(null)}
            >
              ×
            </button>
            <img
              src={selectedImage}
              alt="Gallery image"
              className="max-w-full max-h-[90vh] object-contain"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default PhotoGallerySection;
